import React, { ReactElement, memo } from "react";
import { NativeSyntheticEvent, NativeScrollEvent } from "react-native";

import Header from "@/components/organisms/HeaderWatchList";
import ItemSeparator from "@/components/molecules/ItemSeparator";

import { RefreshControl } from "@/components/atoms/RefreshControl";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { PlayerSearchPlayerFragment } from "@/generated/graphql";

/**
 * Props for FilterResultsView component
 */
interface Props {
  /** Whether the list is being refreshed via pull-to-refresh */
  refreshing: boolean;
  /** Array of player search result fragments to display */
  players: PlayerSearchPlayerFragment[];
  /** Whether to show the filter popover in the header */
  isShowPopoverFilter?: boolean;
  /** Bottom safe area inset in pixels for proper content padding */
  bottomInset: number;
  /** Callback invoked on scroll events for pagination */
  onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  /** Callback invoked when user pulls to refresh the list */
  onRefresh: () => void;
  /** Function to render each player item in the list */
  renderItem: (info: { item: PlayerSearchPlayerFragment }) => ReactElement;
  /** Function to render footer content below the list (e.g., loading indicator) */
  renderListFooterComponent: () => ReactElement | null;
  /** Function to render content when list is empty (e.g., no results message) */
  renderListEmptyComponent: () => ReactElement | null;
}

/**
 * Pure view component for displaying filtered player search results.
 * Uses ScrollView + .map() instead of FlashList to avoid width measurement
 * issues on web where FlashList doesn't correctly measure available width
 * on initial render.
 * @param root0 - The component props
 * @param root0.players - Array of player search result fragments to display
 * @param root0.refreshing - Whether the list is being refreshed via pull-to-refresh
 * @param root0.isShowPopoverFilter - Whether to show the filter popover in the header
 * @param root0.bottomInset - Bottom safe area inset in pixels for proper content padding
 * @param root0.onScroll - Callback invoked on scroll events for pagination
 * @param root0.onRefresh - Callback invoked when user pulls to refresh the list
 * @param root0.renderItem - Function to render each player item in the list
 * @param root0.renderListFooterComponent - Function to render footer content below the list (e.g., loading indicator)
 * @param root0.renderListEmptyComponent - Function to render content when list is empty (e.g., no results message)
 */
const FilterResultsView = ({
  players,
  refreshing,
  isShowPopoverFilter,
  bottomInset,
  onScroll,
  onRefresh,
  renderItem,
  renderListFooterComponent,
  renderListEmptyComponent,
}: Props) => (
  <Stack className="flex-grow pb-4">
    <ScrollView
      className="flex-grow p-3"
      contentContainerStyle={{ paddingBottom: bottomInset }}
      scrollEventThrottle={16}
      onScroll={onScroll}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={["white"]}
          tintColor="white"
        />
      }
    >
      <Header
        isShowPopoverFilter={isShowPopoverFilter}
        watchListVisible={false}
      />
      {players.length === 0
        ? renderListEmptyComponent()
        : players.map((item, index) => (
            <React.Fragment key={`${item.id}-${index}`}>
              {index > 0 && <ItemSeparator />}
              {renderItem({ item })}
            </React.Fragment>
          ))}
      {renderListFooterComponent()}
    </ScrollView>
  </Stack>
);

FilterResultsView.displayName = "FilterResultsView";

export default memo(FilterResultsView);
